import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  useBreakpointValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Card,
  CardBody,
  SimpleGrid,
  Divider,
  HStack,
  Badge,
} from '@chakra-ui/react'
import { getAssetList } from '../services/apiService'

interface Asset {
  id: number
  location: string
  area: number
  appraisalValue: string
  note: string | null
  createdAt: string
}

const Assets: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(false)

  // 숫자 포맷팅 (천단위 콤마)
  const formatNumber = (num: number | string) => {
    return Number(num).toLocaleString()
  }

  const fetchAssets = async () => {
    setLoading(true)
    try {
      const { data: res } = await getAssetList()
      if (res.statusCode === 200) {
        setAssets(res.data.assets)
      }
    } catch (error) {
      console.error('Error fetching assets:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAssets()
  }, [])

  // 총 면적과 총 감정가 계산
  const totalArea = assets.reduce((sum, asset) => sum + asset.area, 0)
  const totalAppraisalValue = assets.reduce((sum, asset) => sum + Number(asset.appraisalValue), 0)

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading
              as="h1"
              size={isMobile ? 'xl' : '2xl'}
              bgGradient="linear(to-r, brand.400, brand.600)"
              bgClip="text"
              fontWeight="bold"
              mb={4}
            >
              {t('자산')}
            </Heading>
            <Text fontSize="lg" color="gray.600">
              {t('자산 정보를 확인하세요')}
            </Text>
          </Box>

          {/* 요약 정보 */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="sm" color="gray.500" mb={1}>총 자산 수</Text>
                <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                  {assets.length} 건
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="sm" color="gray.500" mb={1}>총 면적</Text>
                <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                  {formatNumber(totalArea)} m²
                </Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="sm" color="gray.500" mb={1}>총 감정가</Text>
                <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                  {formatNumber(totalAppraisalValue)} 원
                </Text>
              </CardBody>
            </Card>
          </SimpleGrid>

          <Box
            p={6}
            bg="white"
            borderRadius="lg"
            boxShadow="sm"
          >
            {loading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <VStack spacing={4} align="stretch">
                {isMobile ? (
                  // 모바일: 카드 형태
                  <VStack spacing={4} align="stretch">
                    {assets.length === 0 ? (
                      <Box textAlign="center" py={8}>
                        <Text color="gray.500">등록된 자산이 없습니다.</Text>
                      </Box>
                    ) : (
                      assets.map((asset) => (
                        <Card key={asset.id} size="sm">
                          <CardBody>
                            <VStack align="stretch" spacing={3}>
                              <HStack justify="space-between">
                                <Badge colorScheme="blue" fontSize="md" px={2} py={1}>
                                  {asset.location}
                                </Badge>
                              </HStack>
                              <Divider />
                              <SimpleGrid columns={2} spacing={2} fontSize="sm">
                                <Text color="gray.600">면적:</Text>
                                <Text fontWeight="semibold">{formatNumber(asset.area)} m²</Text>
                                <Text color="gray.600">감정가:</Text>
                                <Text fontWeight="semibold">{formatNumber(asset.appraisalValue)} 원</Text>
                                {asset.note && (
                                  <>
                                    <Text color="gray.600">비고:</Text>
                                    <Text>{asset.note}</Text>
                                  </>
                                )}
                              </SimpleGrid>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))
                    )}
                  </VStack>
                ) : (
                  // 데스크탑: 테이블 형태
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>소재지</Th>
                          <Th isNumeric>면적 (m²)</Th>
                          <Th isNumeric>감정가 (원)</Th>
                          <Th>비고</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assets.length === 0 ? (
                          <Tr>
                            <Td colSpan={4} textAlign="center" py={8}>
                              <Text color="gray.500">등록된 자산이 없습니다.</Text>
                            </Td>
                          </Tr>
                        ) : (
                          assets.map((asset) => (
                            <Tr key={asset.id}>
                              <Td>
                                <Badge colorScheme="blue">{asset.location}</Badge>
                              </Td>
                              <Td isNumeric fontWeight="medium">{formatNumber(asset.area)}</Td>
                              <Td isNumeric fontWeight="medium">{formatNumber(asset.appraisalValue)}</Td>
                              <Td color="gray.600">{asset.note || '-'}</Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default Assets
